import { useMemo } from 'react';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import type { AppliedFilters, MakeType, ModelType } from '~/contracts/common';
import type { SavedSearch } from '~/contracts/common';
import decodeCvnaId from '~/utilities/cvnaId';
import compareArrays from '~/utilities/srp/compareArrays';

export type FilterValue = string | string[] | number | boolean;

type MakeOrModel = MakeType | ModelType;
type FlatMakes = Array<string | string[]>;

function isMakeOrModel(key: string, _: unknown): _ is MakeOrModel[] {
  return key === 'makes';
}

/**
 * sorts filter object for more accurate comparisons:
 * {"min": 15, "max": 100} => {"max": 100, "min": 15}
 */
function sortObjectKeys(obj: Record<string, any>): Record<string, any> {
  const sortedKeys = Object.keys(obj).sort();
  const sortedObj: Record<string, any> = {};

  for (const key of sortedKeys) {
    sortedObj[key] = obj[key];
  }

  return sortedObj;
}

/**
 * reduces the makes filter into a single array:
 * ["Honda", "Civic", "Trim_1", "Trim_2", "Toyota", "Camry"]
 */
function extractMakesModelsTrims(items: MakeOrModel[]): FlatMakes {
  return items.reduce((acc: FlatMakes, m: MakeOrModel) => {
    acc.push(m.name);

    if ('models' in m && m.models) {
      acc.push(...extractMakesModelsTrims(m.models));
    }

    if ('parentModels' in m && m.parentModels) {
      acc.push(...extractMakesModelsTrims(m.parentModels));
    }

    if ('trims' in m && m.trims) {
      acc.push(...m.trims);
    }

    return acc;
  }, []);
}

const filtersToSkip: string[] = new Array<keyof AppliedFilters>('vehicleIds');

/**
 * Flattens all filters into an array that can be compared
 * Range filters are flattened with JSON.stringify
 * Makes is flattened using extractMakesModelsTrims
 */
function flattenFilters(filters: AppliedFilters): FilterValue[] {
  return Object.entries(filters)
    .filter(([key]) => !filtersToSkip.includes(key))
    .flatMap(([key, values]) => {
      if (isMakeOrModel(key, values)) {
        return extractMakesModelsTrims(values);
      }

      if (Array.isArray(values)) {
        return JSON.stringify([...values].sort());
      }

      if (typeof values === 'object') {
        return JSON.stringify(sortObjectKeys(values));
      }

      return JSON.stringify(values);
    });
}

// determines if any savedSearches' filters match the current filters
export function isCurrentSearchSaved(appliedFilters: AppliedFilters) {
  return ({ cvnaId }: SavedSearch): boolean => {
    const savedFilters = decodeCvnaId(cvnaId).filters ?? {};

    // flattening all values into a single array for comparing
    const appliedFiltersToCompare = flattenFilters(appliedFilters);
    const savedFiltersToCompare = flattenFilters(savedFilters);

    return compareArrays(appliedFiltersToCompare, savedFiltersToCompare);
  };
}

export function useCurrentSavedSearchId(
  appliedFilters: AppliedFilters
): number | null {
  const savedSearches = useSavedSearches();

  return useMemo(() => {
    const savedSearch = savedSearches.find(
      isCurrentSearchSaved(appliedFilters)
    );

    return savedSearch ? savedSearch.savedSearchId : null;
  }, [appliedFilters, savedSearches]);
}

export default function useSavedSearchApplied(
  appliedFilters: AppliedFilters
): boolean {
  const savedSearches = useSavedSearches();

  return useMemo(
    () => savedSearches.some(isCurrentSearchSaved(appliedFilters)),
    [appliedFilters, savedSearches]
  );
}
